import {
  Component,
  ContentChildren,
  Input,
  Output,
  QueryList,
  EventEmitter,
  AfterViewInit,
  ViewChild,
  OnDestroy,
  forwardRef,
  TemplateRef,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import {
  ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR
} from '@angular/forms';
import { equals } from 'ramda';
import { BehaviorSubject } from 'rxjs';
import { takeUntil, throttleTime, skip } from 'rxjs/operators';

import { ResizeObserverDirective } from '@sondermind/utilities/directives';
import { BreakpointsService } from '@sondermind/utilities/services';

import { ColumnComponent } from './column.component';
import { SelectByFunction } from './data-table.model';
import { RolloutComponent } from './rollout.component';
import { SummaryComponent } from './summary.component';

@Component({
  selector: 'sondermind-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DataTableComponent), multi: true }
  ],
  standalone: false
})
export class DataTableComponent implements AfterViewInit, OnDestroy, ControlValueAccessor, OnChanges {
  @Input() data: any[];

  private tableWidth: number;
  private destroyed$ = new EventEmitter<void>();

  selectAllControl = new FormControl<boolean | null>(false);
  selectCheckboxes: Array<FormControl<boolean>> = [];
  isSelectAllIndeterminate: boolean = false;

  tableData: any[];

  // this is awful, but it's better than going to the DOM
  // TODO: figure out how to get these out of the css
  checkboxDesktopWidth = 18 + 48; // width + padding
  checkboxMobileWidth = 18 + 24;
  buttonDesktopWidth = 40 + 16; // width + padding
  buttonMobileWidth = 40 + 16;

  @ContentChildren(ColumnComponent)
    columnDefs: QueryList<ColumnComponent>;

  @ContentChildren(RolloutComponent)
    rolloutDefs: QueryList<RolloutComponent>;

  @ContentChildren(SummaryComponent)
    summaryDefs: QueryList<SummaryComponent>;

  @ViewChild(ResizeObserverDirective)
    tableResizeObserver: ResizeObserverDirective;

  @ViewChild('defaultRowTmpl')
    defaultRowTemplate: TemplateRef<any>;

  constructor(
    private breakpoints: BreakpointsService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.initTableData();

    if (changes['data']){
      this.initSelectCheckboxes();
      Promise.resolve().then(() => this.writeValue([]));
    }
  }

  ngAfterViewInit() {
    this.tableResizeObserver.width$.pipe(
      takeUntil(this.destroyed$),
      throttleTime(250),
    ).subscribe((width) => {
      this.tableWidth = width;
    });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  // rows

  initTableData(): void {
    // if the data hasn't loaded yet, just be empty
    // todo - loading indicator?

    if (!this.data) {
      this.tableData = [];
      return;
    }

    // if we're not gouping, just return the data as it's passed in; nothing fancy

    if (!this.isGroupable) {
      this.tableData = this.data;
      return;
    }

    // have to maintain sort order
    // so it can't get returned in an object - instead return just a list of lists
    // the display will just grab the first row with the template to figure out the value

    const groups = [];
    this.data.forEach((row: any) => {
      if (groups.length === 0) {
        groups.push([row]);
        return;
      }

      const rowGroupValue = this.getGroupValue(row);
      const memoGroupValue = this.getGroupValue(groups[groups.length - 1][0]);

      if (equals(rowGroupValue, memoGroupValue)) {
        groups[groups.length - 1].push(row);
      } else {
        groups.push([row]);
      }
    });

    this.tableData = groups;
  }

  // eslint-disable-next-line @typescript-eslint/no-shadow
  rowContext(row: any, index: number = 0, skip = '') {
    return {
      row,
      $implicit: row,
      table: this,
      index,
      defaultRowTemplate: this.defaultRowTemplate,
      skip
    };
  }

  @Input()
    zebra = true;

  @Input()
    striped = true;

  @Input()
    header = true;

  @Input()
    contentBleed = false;

  @Input()
    compressed = false;

  @Input()
    navigationArrow = true;

  // columns

  handleHeaderClick(column: ColumnComponent, _event) {
    if (column.sortable) {
      column.toggleSort();
    }
  }

  getColumnWidth(column: ColumnComponent) {
    if (column.width == null) {
      return '1px';
    }

    const groupColumn = this.getGroupColumn();
    const totalWidths = this.columnDefs.map((cd) => (cd === groupColumn ? 0 : cd.width)).reduce((a, b) => a + b) * 1.0;
    const ratio = column.width / (totalWidths * 1.0);

    const checkboxWidth = this.breakpoints.isMobile ? this.checkboxMobileWidth : this.checkboxDesktopWidth;
    const buttonWidth = this.breakpoints.isMobile ? this.buttonMobileWidth : this.buttonDesktopWidth;

    if (this.tableWidth) {
      const unusedWidth = this.tableWidth
        - (this.isSelectable ? checkboxWidth : 0)
        - (this.isRolloutable ? checkboxWidth : 0)
        - (this.hasNavigationArrow ? buttonWidth : 0);

      // if we set the sizes in px, it'll keep the table from being able to shrink
      // because the explicit widths will override the  table's width when shrinking, forcing to stay larger

      // instead, return the pecentage scaled back up to the full table width
      return `${Math.round((100 * ratio * unusedWidth) / this.tableWidth)}%`;
    }
    return `${ratio * 100}%`;
  }

  // grouping

  @Input()
    groupBy: SelectByFunction<any>;

  get isGroupable() {
    return this.groupBy != null;
  }

  getGroupValue(row: any): any {
    if (this.groupBy == null) {
      return null;
    }

    if (typeof this.groupBy === 'function') {
      return this.groupBy(row);
    }

    return row[this.groupBy];
  }

  getGroupColumn() {
    if (!this.isGroupable) {
      return null;
    }

    const column = this.columnDefs.find((cd) => cd.key === this.groupBy);
    return column;
  }

  getGroupedColumnCount() {
    const otherColumnCount = this.isGroupable ? this.columnDefs.length - 1 : this.columnDefs.length;
    return otherColumnCount + (this.isSelectable ? 1 : 0) + (this.hasNavigationArrow ? 1 : 0);
  }

  get totalColumnCount(): number {
    return (
      this.columnDefs.length
      + (this.isSelectable ? 1 : 0)
      + (this.isRolloutable ? 1 : 0)
      + (this.hasNavigationArrow ? 1 : 0)
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-shadow
  groupContext(rows, skip) {
    return { rows, skip };
  }

  // selections

  private selectionsSubj = new BehaviorSubject<any[]>([]);

  @Input()
    selectBy: SelectByFunction<any>;

  @Input()
    selectionDisabledBy: SelectByFunction<any>;

  @Input()
    selectionDisabled = false;

  @Input()
  get selections() {
    return this.selectionsSubj.value;
  }

  set selections(value: any[]) {
    if (value == null) {
      return;
    }
    this.selectionsSubj.next(value);
  }

  @Output()
    selectionsChange = this.selectionsSubj.asObservable().pipe(skip(1));

  get isSelectable() {
    return this.selectBy != null;
  }

  @Input()
    rolloutBy: SelectByFunction<any>;

  rolledOutRows: any[] = [];

  get isRolloutable(): boolean {
    return this.rolloutBy != null;
  }

  private getSelector<T>(row: T, selectBy: SelectByFunction<T>) {
    if (
      typeof selectBy === 'symbol'
      || typeof selectBy === 'number'
      || typeof selectBy === 'string'
    ) {
      return row[selectBy];
    }

    if (
      typeof selectBy === 'function'
    ) {
      return selectBy(row);
    }

    // wat?
    // eslint-disable-next-line no-console
    console.error('unsupported selectByFunction: ', selectBy);
    return undefined;
  }

  selectedRow(row: any) {
    if (!this.isSelectable) {
      return false;
    }

    const selector = this.getSelector(row, this.selectBy);
    return this.selections.includes(selector);
  }

  selectableRow(row: any) {
    if (this.selectionDisabled) {
      return false;
    }

    if (this.selectionDisabledBy) {
      const disabled = this.getSelector(row, this.selectionDisabledBy);
      if (disabled) {
        return false;
      }
    }

    return true;
  }

  selectRow(row: any, selected: boolean | 'toggle'): void {
    if (!this.isSelectable) return;

    if (this.selectionDisabledBy && this.getSelector(row, this.selectionDisabledBy)) {
      return;
    }

    const selector = this.getSelector(row, this.selectBy);
    const isInSelections = this.selections.includes(selector);

    // Toggle selection if needed
    // eslint-disable-next-line no-param-reassign
    selected = selected === 'toggle' ? !isInSelections : selected;

    if (selected && isInSelections) return;

    // Add if selected or remove if deselected
    this.selections = selected
      ? [...this.selections, selector]
      : this.selections.filter((item) => item !== selector);
  }

  /*
   * Determine if a rollout is associated with a given row.
   * If it is, then add the rollout to the row.
   * If not, do nothing and return false.
   */
  rolledOutRow(row: any): boolean {
    if (!this.isRolloutable) return false;

    const selector = this.getSelector(row, this.rolloutBy);
    return this.rolledOutRows.includes(selector);
  }

  toggleRowRollout(row: any): void {
    if (!this.isRolloutable) return;

    const selector = this.getSelector(row, this.rolloutBy);

    if (this.rolledOutRows.includes(selector)) {
      // remove if it is included at the moment
      this.rolledOutRows = this.rolledOutRows.filter((sel) => sel !== selector);
    } else {
      // add it if it was not
      this.rolledOutRows = [...this.rolledOutRows, selector];
    }
  }

  clickRolloutChevron(row: any, event: MouseEvent): void {
    this.toggleRowRollout(row);
    event.stopPropagation();
  }

  // row template
  @Input()
    rowTemplate: TemplateRef<any> = null;

  @Input()
    rowTemplateIf = null;

  isRowTemplate(row: any): boolean {
    if (this.rowTemplate) {
      if (this.rowTemplateIf) {
        if (typeof this.rowTemplateIf === 'string') {
          return row[this.rowTemplateIf];
        // eslint-disable-next-line no-else-return
        } else if (typeof this.rowTemplateIf === 'function') {
          return this.rowTemplateIf(row);
        }
      }

      return true;
    }

    return false;
  }

  // control value accessor for selections

  writeValue(selections: any[]): void {
    this.selections = selections;
  }

  registerOnChange(fn: any): void {
    this.selectionsChange.pipe(takeUntil(this.destroyed$)).subscribe(fn);
  }

  registerOnTouched(_fn: any): void {
    // we don't support focus specifically
  }

  setDisabledState?(isDisabled: boolean): void {
    this.selectionDisabled = isDisabled;
  }

  // navigable
  // a table is navigable if something's observing (navigated)

  @Output()
    navigated = new EventEmitter<any>();

  @Input('navigationDisabled')
    navigationDisabledFlag: boolean | ((row: any) => boolean);

  get isNavigable() {
    return this.navigated.observers.length > 0;
  }

  get hasNavigationArrow() {
    return this.isNavigable && this.navigationArrow;
  }

  navigableRow(row: any) {
    // this is awkward (returning the opposite of the flag),
    // but disabled is a better flag

    if (!this.navigationDisabledFlag) {
      return true; // no disabled flag set, or explicitly false, navigable
    }

    if (this.navigationDisabledFlag === true) {
      return false; // explicitly disabled, not navigable
    }

    // conditionally disabled
    return !this.navigationDisabledFlag(row);
  }

  navigateToRow(row: any) {
    if (this.navigableRow(row)) {
      this.navigated.next(row);
    }
  }

  // summary

  get showFooter() {
    return this.summaryDefs && this.summaryDefs.length > 0;
  }

  // event handlers
  @Input()
    modal: boolean = false;
  @Output()
    rowClicked = new EventEmitter();
  rowClick = false ;

  handleRowClick(row: any, _event: Event): void {
    if (this.isNavigable) {
      this.navigated.next(row);
    }

    if (this.isSelectable) {
      const rowIndex = this.tableData.findIndex((item) => item === row);
      if (rowIndex !== -1 && this.selectCheckboxes[rowIndex]) {
        const isChecked = !this.selectCheckboxes[rowIndex].value;
        this.selectCheckboxes[rowIndex].setValue(isChecked);
      }
    }

    if (this.modal){
      this.rowClicked.emit(row);
    }
  }

  /**
   * Check if all selectCheckboxes are disabled.
   * @returns {boolean}
  */
  allCheckboxesDisabled(): boolean {
    return this.selectCheckboxes.every((control) => control.disabled);
  }

  /**
   * Check if some selectable rows are selected.
   * @returns {boolean}
  */
  someRowsSelected(): boolean {
    return this.selectCheckboxes.some((control) => !control.disabled && control.value === true);
  }

  /**
   * Check if some selectable rows are selected
   * and allRowsSelected is false
   * @returns {boolean}
  */
  indeterminateState(): boolean {
    return this.someRowsSelected() && !this.allRowsSelected();
  }

  /**
   * Check if all selectable rows are selected.
   * @returns {boolean}
  */
  private allRowsSelected(): boolean {
    return this.selectCheckboxes.every((control) => control.disabled || control.value === true);
  }

  private initSelectCheckboxes() {
    this.selectCheckboxes = [];
    this.selectAllControl.setValue(false);

    this.tableData.forEach((row: any) => {
      const disabled = !this.selectableRow(row);
      const control = new FormControl({ value: false, disabled });
      this.selectCheckboxes.push(control);

      control.valueChanges
        .pipe(takeUntil(this.destroyed$))
        .subscribe((isChecked) => {
          this.selectRow(row, isChecked);
          this.updateSelectAllControl();
        });
    });

    if (this.allCheckboxesDisabled()) {
      this.selectAllControl.disable({ emitEvent: false });
    } else {
      this.selectAllControl.enable({ emitEvent: false });
    }
  }

  private updateSelectAllControl() {
    this.isSelectAllIndeterminate = this.indeterminateState();
    this.selectAllControl.setValue(this.allRowsSelected());
  }

  toggleSelectAll() {
    this.isSelectAllIndeterminate = false;
    const isChecked = this.selectAllControl.value !== true;
    this.tableData.forEach((row: any, i: number) => {
      if (this.selectableRow(row)) {
        this.selectCheckboxes[i].setValue(isChecked, { emitEvent: false });
        this.selectRow(row, isChecked);
      }
    });
  }
}

export const Declarations = [
  DataTableComponent,
  ColumnComponent,
  SummaryComponent,
  RolloutComponent
];
